import React, { useState, useEffect, useMemo } from "react";
import { Package, CalendarClock } from "lucide-react";
import { useTranslation } from "react-i18next";
import betaAPI from "v2/api";
import { showToast } from "utils/toast";
import ModalShell from "./ModalShell";
import { TextField, TextAreaField } from "./FormField";
import { formatApiError, getIsoYearWeekCode } from "./constants";

const emptyForm = {
  code: "",
  planned_quantity: "",
  hw_revision: "W1",
  pcba_revision: "SIT-PCBA-001",
  cm_name: "",
  manufacture_start_date: "",
  manufacture_end_date: "",
  notes: "",
};

/**
 * Create or edit a manufacturing batch.
 * Pass `batch` to edit (PATCH); omit it to create (POST).
 */
const BatchFormModal = ({ show, onClose, batch, onSaved }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(batch);
  // Current ISO year+week, so we can pre-fill the code for new batches.
  const currentCode = useMemo(() => getIsoYearWeekCode(), []);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show) return;
    setError(null);
    if (batch) {
      setForm({
        code: batch.code ?? "",
        planned_quantity: batch.planned_quantity ?? "",
        hw_revision: batch.hw_revision ?? "W1",
        pcba_revision: batch.pcba_revision ?? "SIT-PCBA-001",
        cm_name: batch.cm_name ?? "",
        manufacture_start_date: batch.manufacture_start_date ?? "",
        manufacture_end_date: batch.manufacture_end_date ?? "",
        notes: batch.notes ?? "",
      });
    } else {
      // New batch: default the code to this week so users don't have to
      // work out the week number themselves.
      setForm({ ...emptyForm, code: currentCode });
    }
  }, [show, batch, currentCode]);

  const set = (key) => (value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const quantityNum = parseInt(form.planned_quantity, 10);
  const isValid =
    form.code.trim().length > 0 &&
    Number.isInteger(quantityNum) &&
    quantityNum >= 1;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSaving(true);
    setError(null);

    // Only send fields with values; backend keeps defaults / existing values.
    const body = {
      code: form.code.trim(),
      planned_quantity: quantityNum,
      hw_revision: form.hw_revision.trim() || undefined,
      pcba_revision: form.pcba_revision.trim() || undefined,
      cm_name: form.cm_name.trim() || undefined,
      manufacture_start_date: form.manufacture_start_date || null,
      manufacture_end_date: form.manufacture_end_date || null,
      notes: form.notes,
    };

    try {
      const res = isEdit
        ? await betaAPI.updateBatch({ batchId: batch.id, body })
        : await betaAPI.createBatch({ body });
      showToast.success(
        isEdit
          ? t("admin.manufacturing.batchForm.updated")
          : t("admin.manufacturing.batchForm.created")
      );
      onSaved?.(res.data);
      onClose();
    } catch (err) {
      setError(
        formatApiError(err, t("admin.manufacturing.batchForm.saveFailed"))
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      icon={Package}
      title={
        isEdit
          ? t("admin.manufacturing.batchForm.editTitle")
          : t("admin.manufacturing.batchForm.newTitle")
      }
      subtitle={
        isEdit
          ? t("admin.manufacturing.batchForm.editSubtitle", {
              code: batch?.code,
            })
          : t("admin.manufacturing.batchForm.newSubtitle")
      }
      submitLabel={
        isEdit
          ? t("admin.manufacturing.batchForm.saveChanges")
          : t("admin.manufacturing.batchForm.createBatch")
      }
      submitting={saving}
      submitDisabled={!isValid}
      onSubmit={handleSubmit}
      error={error}
    >
      <div className="grid grid-cols-2 gap-4">
        {/* Batch code — auto-filled to the current ISO week */}
        <div>
          <TextField
            label={t("admin.manufacturing.batchForm.batchCode")}
            value={form.code}
            onChange={set("code")}
            placeholder={currentCode}
            required
            maxLength={4}
          />
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className="text-xs text-[#9CA3AF]">
              {t("admin.manufacturing.batchForm.batchCodeHint")}
            </p>
            {!isEdit && form.code !== currentCode && (
              <button
                type="button"
                onClick={() => set("code")(currentCode)}
                className="inline-flex items-center gap-1 text-xs font-medium text-[#E07A3A] hover:underline"
              >
                <CalendarClock size={12} />
                {t("admin.manufacturing.batchForm.useThisWeek", {
                  code: currentCode,
                })}
              </button>
            )}
          </div>
        </div>

        <TextField
          label={t("admin.manufacturing.batchForm.plannedQuantity")}
          value={form.planned_quantity}
          onChange={set("planned_quantity")}
          placeholder="100"
          type="number"
          required
          hint={t("admin.manufacturing.batchForm.plannedQuantityHint")}
        />
        <TextField
          label={t("admin.manufacturing.batchForm.hwRevision")}
          value={form.hw_revision}
          onChange={set("hw_revision")}
          placeholder="W1"
          hint={t("admin.manufacturing.batchForm.hwRevisionHint")}
        />
        <TextField
          label={t("admin.manufacturing.batchForm.pcbaRevision")}
          value={form.pcba_revision}
          onChange={set("pcba_revision")}
          placeholder="SIT-PCBA-001"
          hint={t("admin.manufacturing.batchForm.pcbaRevisionHint")}
        />
        <TextField
          className="col-span-2"
          label={t("admin.manufacturing.batchForm.contractManufacturer")}
          value={form.cm_name}
          onChange={set("cm_name")}
          placeholder={t(
            "admin.manufacturing.batchForm.contractManufacturerPlaceholder"
          )}
          hint={t("admin.manufacturing.batchForm.contractManufacturerHint")}
        />
        <TextField
          label={t("admin.manufacturing.batchForm.manufactureStart")}
          value={form.manufacture_start_date}
          onChange={set("manufacture_start_date")}
          type="date"
          hint={t("admin.manufacturing.batchForm.manufactureStartHint")}
        />
        <TextField
          label={t("admin.manufacturing.batchForm.manufactureEnd")}
          value={form.manufacture_end_date}
          onChange={set("manufacture_end_date")}
          type="date"
          hint={t("admin.manufacturing.batchForm.manufactureEndHint")}
        />
        <TextAreaField
          className="col-span-2"
          label={t("admin.manufacturing.batchForm.notes")}
          value={form.notes}
          onChange={set("notes")}
          placeholder={t("admin.manufacturing.batchForm.notesPlaceholder")}
        />
      </div>
    </ModalShell>
  );
};

export default BatchFormModal;
